import React, { useState, useEffect } from 'react';
import { database, ref, get, update, set } from '../../firebase';
import { 
  Users, 
  Filter, 
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Search,
  Shield,
  ShieldOff,
  DollarSign,
  X,
  AlertCircle,
  Check
} from 'lucide-react';

const LevelReport = () => {
  const [levelData, setLevelData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [stats, setStats] = useState({
    totalCommission: 0,
    todayCommission: 0,
    blockedCommission: 0,
    level1: 0, level2: 0, level3: 0, level4: 0, level5: 0
  });
  const [expandedRows, setExpandedRows] = useState([]);
  const [processingAction, setProcessingAction] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchLevelData();
  }, []);

  const fetchLevelData = async () => {
    try {
      setLoading(true);
      
      const levelRef = ref(database, 'levelIncomes');
      const snapshot = await get(levelRef);
      
      const levelArray = [];
      let totalCommission = 0;
      let todayCommission = 0;
      let blockedCommission = 0;
      const levelCounts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      const today = new Date().toISOString().split('T')[0];
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.entries(data).forEach(([date, users]) => {
          Object.entries(users).forEach(([userId, incomes]) => {
            Object.entries(incomes).forEach(([incomeId, income]) => {
              const amount = parseFloat(income.amount) || 0;
              const level = parseInt(income.level) || 1;
              
              const levelItem = {
                id: incomeId,
                date: date,
                userId: income.userId || userId,
                userName: income.userName || 'Unknown',
                level: level,
                amount: amount,
                status: income.status || 'credited',
                fromUserId: income.fromUserId,
                fromUserName: income.fromUserName,
                packageName:
                  income.packageName ||
                  income.fromPackageName ||
                  income.package ||
                  income.fromPackage ||
                  'Package',
                description: income.description,
                createdAt: income.date || income.createdAt,
                originalPath: `levelIncomes/${date}/${userId}/${incomeId}`
              };
              
              levelArray.push(levelItem);
              
              if (income.status !== 'blocked') {
                totalCommission += amount;
                levelCounts[level] = (levelCounts[level] || 0) + amount;
                
                if (date === today) {
                  todayCommission += amount;
                }
              } else {
                blockedCommission += amount;
              }
            });
          });
        });
      }
      
      levelArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setLevelData(levelArray);
      setStats({
        totalCommission,
        todayCommission,
        blockedCommission,
        level1: levelCounts[1],
        level2: levelCounts[2],
        level3: levelCounts[3],
        level4: levelCounts[4],
        level5: levelCounts[5]
      });
    } catch (error) {
      console.error('Error fetching level data:', error);
    } finally {
      setLoading(false);
    }
  };

  const showConfirmation = (income, action) => {
    setConfirmData({
      ...income,
      action: action
    });
    setShowConfirmDialog(true);
  };

  const handleActionConfirm = async () => {
    if (!confirmData) return;
    
    try {
      setProcessingAction(confirmData.id);
      
      const newStatus = confirmData.action === 'block' ? 'blocked' : 'credited';
      const timestamp = new Date().toISOString();
      
      // Update the level income status
      await update(ref(database, confirmData.originalPath), {
        status: newStatus,
        updatedAt: timestamp,
        lastUpdatedBy: 'admin'
      });
      
      // Record admin action in user's data
      const userDate = confirmData.date;
      const actionId = `ACT${Date.now()}`;
      
      // Get user's dbKey from users database
      const usersRef = ref(database, `users/${userDate}`);
      const usersSnapshot = await get(usersRef);
      let userDbKey = null;
      
      if (usersSnapshot.exists()) {
        const users = usersSnapshot.val();
        for (const [dbKey, user] of Object.entries(users)) {
          if (user.sponsorId === confirmData.userId) {
            userDbKey = dbKey;
            break;
          }
        }
      }
      
      if (userDbKey) {
        const userActionRef = ref(database, `users/${userDate}/${userDbKey}/adminActions/${actionId}`);
        await set(userActionRef, {
          id: actionId,
          type: 'level_income',
          incomeId: confirmData.id,
          action: confirmData.action,
          previousStatus: confirmData.status,
          newStatus: newStatus,
          amount: confirmData.amount,
          level: confirmData.level,
          adminId: 'admin',
          timestamp: timestamp,
          note: `${confirmData.action} Level ${confirmData.level} commission`
        });
      }
      
      // Update local state
      setLevelData(prev => prev.map(item => {
        if (item.id === confirmData.id) {
          return { ...item, status: newStatus };
        }
        return item;
      }));
      
      // Update stats
      fetchLevelData();
      
      // Show success
      setSuccessMessage(
        `${confirmData.action === 'block' ? 'Blocked' : 'Unblocked'} Level ${confirmData.level} commission!`
      );
      setShowSuccessDialog(true);
      
    } catch (error) {
      console.error('Error updating level status:', error);
    } finally {
      setProcessingAction(null);
      setShowConfirmDialog(false);
      setConfirmData(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLevelBadge = (level) => {
    const colors = [
      'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'bg-green-500/20 text-green-400 border-green-500/30',
      'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'bg-red-500/20 text-red-400 border-red-500/30'
    ];
    const colorIndex = Math.min(level - 1, 4);
    return {
      label: `Level ${level}`,
      color: colors[colorIndex] || colors[0]
    };
  };

  const filterData = () => {
    let filtered = levelData;
    
    if (dateFilter !== 'all') {
      const now = new Date();
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date || item.createdAt);
        switch(dateFilter) {
          case 'today':
            return itemDate.toDateString() === now.toDateString();
          case 'week':
            const weekAgo = new Date();
            weekAgo.setDate(weekAgo.getDate() - 7);
            return itemDate > weekAgo;
          case 'month':
            const monthAgo = new Date();
            monthAgo.setDate(monthAgo.getDate() - 30);
            return itemDate > monthAgo;
          default:
            return true;
        }
      });
    }
    
    if (levelFilter !== 'all') {
      filtered = filtered.filter(item => item.level === parseInt(levelFilter));
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.userName?.toLowerCase().includes(term) ||
        item.userId?.toLowerCase().includes(term) ||
        item.fromUserName?.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  };

  const filteredData = filterData();

  const toggleRowExpansion = (id) => {
    setExpandedRows(prev =>
      prev.includes(id)
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const Dialog = ({ isOpen, onClose, children, title }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
        <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-800">
            <h3 className="text-xl font-bold text-white">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              disabled={processingAction}
            >
              <X size={24} />
            </button>
          </div>
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      <h1 className="text-2xl font-bold text-white mb-2">Level Commission Report</h1>
      <p className="text-gray-400 mb-6">Level-wise commission distribution with admin controls</p>

      {/* Level Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Total</div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.totalCommission)}</div>
        </div>
        {[1, 2, 3, 4, 5].map(level => (
          <div key={level} className="card-glow p-3 bg-gray-800/50 rounded-xl">
            <div className="text-gray-400 text-xs mb-1">Level {level}</div>
            <div className="text-lg font-bold text-blue-400">{formatCurrency(stats[`level${level}`])}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="card-glow p-4 mb-6 bg-gray-800/50 rounded-xl">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by user name or ID"
                className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
            
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="all">All Levels</option>
              <option value="1">Level 1</option>
              <option value="2">Level 2</option>
              <option value="3">Level 3</option>
              <option value="4">Level 4</option>
              <option value="5">Level 5</option>
            </select>
            
            <button
              onClick={fetchLevelData}
              className="px-4 py-2 bg-primary hover:bg-primary/80 rounded-lg text-white flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block card-glow overflow-hidden mb-6 bg-gray-800/50 rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr className="text-sm">
                <th className="py-3 px-4 text-left text-gray-400">Date</th>
                <th className="py-3 px-4 text-left text-gray-400">User</th>
                <th className="py-3 px-4 text-left text-gray-400">Level</th>
                <th className="py-3 px-4 text-left text-gray-400">From User</th>
                <th className="py-3 px-4 text-left text-gray-400">Amount</th>
                <th className="py-3 px-4 text-left text-gray-400">Package</th>
                <th className="py-3 px-4 text-left text-gray-400">Status</th>
                {/* <th className="py-3 px-4 text-left text-gray-400">Actions</th> */}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30">
              {filteredData.map((item) => {
                const levelBadge = getLevelBadge(item.level);
                const isBlocked = item.status === 'blocked';
                
                return (
                  <tr key={item.id} className="hover:bg-gray-700/20">
                    <td className="py-3 px-4 text-sm text-gray-300">
                      {formatDate(item.createdAt)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-white font-medium">{item.userName}</div>
                      <div className="text-xs text-gray-400">{item.userId}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs border ${levelBadge.color}`}>
                        {levelBadge.label}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="text-white text-sm">{item.fromUserName}</div>
                      <div className="text-xs text-gray-400">{item.fromUserId}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`font-bold ${isBlocked ? 'text-red-400' : 'text-green-400'}`}>
                        {formatCurrency(item.amount)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-300">
                      {item.packageName || 'Package'}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs border ${
                        isBlocked 
                          ? 'bg-red-500/20 text-red-400 border-red-500/30' 
                          : 'bg-green-500/20 text-green-400 border-green-500/30'
                      }`}>
                        {item.status}
                      </span>
                    </td>
                    {/* <td className="py-3 px-4">
                      <button
                        onClick={() => showConfirmation(item, isBlocked ? 'unblock' : 'block')}
                        disabled={processingAction === item.id}
                        className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1 ${
                          processingAction === item.id
                            ? 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
                            : isBlocked
                            ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30'
                            : 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30'
                        }`}
                      >
                        {processingAction === item.id ? (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                        ) : isBlocked ? (
                          <>
                            <ShieldOff className="h-3.5 w-3.5" />
                            Unblock
                          </>
                        ) : (
                          <>
                            <Shield className="h-3.5 w-3.5" />
                            Block
                          </>
                        )}
                      </button>
                    </td> */}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile View */}
      <div className="lg:hidden space-y-4">
        {filteredData.map((item) => {
          const levelBadge = getLevelBadge(item.level);
          const isBlocked = item.status === 'blocked';
          const isExpanded = expandedRows.includes(item.id);
          
          return (
            <div key={item.id} className="card-glow overflow-hidden bg-gray-800/50 rounded-xl">
              <button
                onClick={() => toggleRowExpansion(item.id)}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-700/50"
              >
                <div className="text-left flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium text-white">{item.userName}</div>
                      <div className="text-xs text-gray-400">
                        {levelBadge.label} • {formatDate(item.createdAt)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${isBlocked ? 'text-red-400' : 'text-green-400'}`}>
                        {formatCurrency(item.amount)}
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border mt-1 ${levelBadge.color}`}>
                        {levelBadge.label}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="ml-2">
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4 text-primary" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-primary" />
                  )}
                </div>
              </button>
             
              {isExpanded && (
                <div className="p-4 border-t border-gray-700">
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-gray-400 text-xs mb-1">From User</div>
                        <div className="text-white text-sm">{item.fromUserName}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs mb-1">Package</div>
                        <div className="text-white text-sm">{item.packageName}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs mb-1">Status</div>
                        <div className={`text-sm ${isBlocked ? 'text-red-400' : 'text-green-400'}`}>
                          {item.status}
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-3 border-t border-gray-700/30">
                      <button
                        onClick={() => showConfirmation(item, isBlocked ? 'unblock' : 'block')}
                        disabled={processingAction === item.id}
                        className={`w-full py-2 rounded-lg flex items-center justify-center gap-2 ${
                          processingAction === item.id
                            ? 'bg-gray-500/20 text-gray-400 cursor-not-allowed'
                            : isBlocked
                            ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30'
                            : 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30'
                        }`}
                      >
                        {processingAction === item.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        ) : isBlocked ? (
                          <>
                            <ShieldOff className="h-4 w-4" />
                            Unblock Commission
                          </>
                        ) : (
                          <>
                            <Shield className="h-4 w-4" />
                            Block Commission
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        
        {filteredData.length === 0 && (
          <div className="text-center py-12 card-glow bg-gray-800/50 rounded-xl">
            <div className="text-4xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-white mb-2">No Level Commissions</h3>
            <p className="text-gray-400">Try adjusting your search filters</p>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog 
        isOpen={showConfirmDialog} 
        onClose={() => !processingAction && setShowConfirmDialog(false)}
        title="Confirm Action"
      >
        {confirmData && (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="text-red-400" size={32} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                {confirmData.action === 'block' ? 'Block Commission' : 'Unblock Commission'}
              </h3>
              <p className="text-gray-300">
                Are you sure you want to {confirmData.action} this Level {confirmData.level} commission?
              </p>
            </div>
            
            <div className="bg-gray-800/50 rounded-xl p-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">User:</span>
                  <span className="text-white font-bold">{confirmData.userName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Level:</span>
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs border ${getLevelBadge(confirmData.level).color}`}>
                    Level {confirmData.level}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount:</span>
                  <span className="text-green-400 font-bold">{formatCurrency(confirmData.amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">From User:</span>
                  <span className="text-white">{confirmData.fromUserName}</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => !processingAction && setShowConfirmDialog(false)}
                disabled={processingAction}
                className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-xl text-white font-bold"
              >
                Cancel
              </button>
              <button
                onClick={handleActionConfirm}
                disabled={processingAction}
                className={`flex-1 py-3 rounded-xl text-white font-bold ${
                  confirmData.action === 'block'
                    ? 'bg-red-500 hover:bg-red-600'
                    : 'bg-green-500 hover:bg-green-600'
                }`}
              >
                {processingAction ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        )}
      </Dialog>

      {/* Success Dialog */}
      <Dialog 
        isOpen={showSuccessDialog} 
        onClose={() => setShowSuccessDialog(false)}
        title="Success"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="text-green-400" size={32} />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Action Completed!</h3>
          <p className="text-gray-300 mb-6">{successMessage}</p>
          <button
            onClick={() => setShowSuccessDialog(false)}
            className="w-full py-3 bg-primary hover:bg-primary/80 rounded-xl text-white font-bold"
          >
            OK
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default LevelReport;
